import React, { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Collapse,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Email as EmailIcon, Phone as PhoneIcon, Sms as SmsIcon, Close as CloseIcon } from '@mui/icons-material';
import { InfiniteData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useAdminData } from '../../../contexts/AdminDataContext';
import { resolveBookingUxConfig } from '../../../utils/bookingUxConfig';
import { getCurrentBusinessConfig } from '../../../firebase/firebase-delegate';
import { BusinessConfig, Customer, Employee } from '../../../types';
import { fetchCustomersPage, getImageFromStorage } from '../../../firebase/firebase-delegate';
import { dayjs } from '../../../utils/dayjs-config';
import { getCachedAssetUrl } from '../../../utils/assetCache';
import { WEBSITE_ID } from '../../../projectConfig';
import { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore';

type CustomerPage = {
  customers: Customer[];
  lastDoc: QueryDocumentSnapshot<DocumentData> | null;
  hasMore: boolean;
};

const PAGE_SIZE = 50;
const ALL_EMPLOYEES_OPTION = 'ALL_EMPLOYEES';

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);

const getEmployeeInitials = (employee: Employee) =>
  `${employee.firstName?.[0] ?? ''}${employee.lastName?.[0] ?? ''}`.toUpperCase() || 'E';

const formatLastAppointmentDate = (customer: Customer | null) => {
  if (!customer?.lastAppointmentDate) return '—';
  return dayjs(customer.lastAppointmentDate.toDate()).format('MMM D, YYYY');
};

const formatPhoneDisplay = (phone?: string | null) => {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 10) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits.startsWith('1')) {
    return `1-${digits.slice(1, 4)}-${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return phone;
};

const CustomersPage: React.FC = () => {
  const { currentEmployeeQuery, employeesQuery } = useAdminData();
  const businessConfigQuery = useQuery<BusinessConfig | null>({
    queryKey: ['businessConfig'],
    queryFn: () => getCurrentBusinessConfig(),
  });
  const bookingUxConfig = resolveBookingUxConfig(businessConfigQuery.data);
  const resourceSingular = bookingUxConfig.terminology.resourceSingular;
  const resourcePlural = bookingUxConfig.terminology.resourcePlural;
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | undefined>();
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [profilePictures, setProfilePictures] = useState<Record<string, string>>({});
  const [searchTerm, setSearchTerm] = useState('');
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));

  const currentEmployee = currentEmployeeQuery.data;
  const employees = employeesQuery.data ?? [];
  const isAdmin = currentEmployee?.admin === true;

  useEffect(() => {
    if (!currentEmployee?.id || selectedEmployeeId) return;
    setSelectedEmployeeId(isAdmin ? ALL_EMPLOYEES_OPTION : currentEmployee.id);
  }, [currentEmployee?.id, isAdmin, selectedEmployeeId]);

  useEffect(() => {
    let isMounted = true;
    const fetchProfilePictures = async () => {
      if (employees.length === 0) {
        setProfilePictures({});
        return;
      }

      const newPictures: Record<string, string> = {};
      await Promise.all(
        employees.map(async (employee) => {
          const picturePath = employee.profilePictureImagePath;
          if (!picturePath) return;
          try {
            const url = await getCachedAssetUrl(
              picturePath,
              () => getImageFromStorage(picturePath)
            );
            if (url) {
              newPictures[employee.id] = url;
            }
          } catch (error) {
            console.error('Failed to load employee profile picture', error);
          }
        })
      );

      if (isMounted) {
        setProfilePictures(newPictures);
      }
    };

    fetchProfilePictures();
    return () => {
      isMounted = false;
    };
  }, [employees]);

  const isAllEmployees = isAdmin && selectedEmployeeId === ALL_EMPLOYEES_OPTION;
  const filterEmployeeId = isAllEmployees ? null : selectedEmployeeId ?? currentEmployee?.id ?? null;
  const filterKey = isAllEmployees
    ? ALL_EMPLOYEES_OPTION
    : filterEmployeeId ?? 'NO_EMPLOYEE';

  const customersQuery = useInfiniteQuery<
    CustomerPage,
    Error,
    InfiniteData<CustomerPage>,
    ['customers', string, string],
    QueryDocumentSnapshot<DocumentData> | null
  >({
    queryKey: ['customers', WEBSITE_ID, filterKey],
    enabled: Boolean(filterKey && filterKey !== 'NO_EMPLOYEE'),
    queryFn: ({ pageParam }) =>
      fetchCustomersPage({
        employeeId: filterEmployeeId,
        pageSize: PAGE_SIZE,
        cursor: pageParam ?? null,
      }),
    initialPageParam: null,
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.lastDoc ?? undefined : undefined),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const customers = useMemo(
    () => customersQuery.data?.pages.flatMap((page) => page.customers) ?? [],
    [customersQuery.data]
  );

  const filteredCustomers = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return customers;
    const termDigits = term.replace(/\D/g, '');
    return customers.filter((customer) => {
      const firstName = (customer.firstName ?? '').toLowerCase();
      const lastName = (customer.lastName ?? '').toLowerCase();
      const fullName = `${firstName} ${lastName}`.trim();
      const email = (customer.email ?? '').toLowerCase();
      const phone = (customer.phone ?? '').toLowerCase();
      const phoneDigits = phone.replace(/\D/g, '');
      return (
        firstName.includes(term) ||
        lastName.includes(term) ||
        fullName.includes(term) ||
        email.includes(term) ||
        phone.includes(term) ||
        (termDigits.length > 0 && phoneDigits.includes(termDigits))
      );
    });
  }, [customers, searchTerm]);

  useEffect(() => {
    setSelectedCustomerId(null);
  }, [filterKey]);

  useEffect(() => {
    if (!selectedCustomerId) return;
    if (!customers.some((customer) => customer.id === selectedCustomerId)) {
      setSelectedCustomerId(null);
    }
  }, [customers, selectedCustomerId]);

  const selectedCustomer = filteredCustomers.find((customer) => customer.id === selectedCustomerId) ?? null;
  const showEmployeeSelector = isAdmin && employees.length > 0;

  const handleCall = (phone?: string | null) => {
    if (!phone) return;
    window.open(`tel:${phone}`, '_self');
  };

  const handleText = (phone?: string | null) => {
    if (!phone) return;
    window.open(`sms:${phone}`, '_self');
  };

  const handleEmail = (email?: string | null) => {
    if (!email) return;
    window.open(`mailto:${email}`, '_self');
  };

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', width: '100%' }}>
      <Stack spacing={2}>
        {showEmployeeSelector && (
          <Box display="flex" justifyContent={{ xs: 'center', md: 'flex-start' }}>
            <TextField
              select
              size="small"
              label={resourceSingular}
              value={selectedEmployeeId ?? ''}
              onChange={(event) => setSelectedEmployeeId(event.target.value)}
              SelectProps={{
                renderValue: (value) => {
                  if (!value) {
                    return (
                      <Typography variant="body1" color="text.secondary">
                        {`Select ${resourceSingular.toLowerCase()}`}
                      </Typography>
                    );
                  }
                  const id = value as string;
                  const isAll = id === ALL_EMPLOYEES_OPTION;
                  const employee = employees.find((item) => item.id === id);
                  const avatarSrc = !isAll && employee ? profilePictures[employee.id] : undefined;
                  const label = isAll
                    ? `All ${resourcePlural}`
                    : employee
                      ? `${employee.firstName} ${employee.lastName}`
                      : '';

                  return (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      {!isAll && (
                        <Avatar
                          src={avatarSrc}
                          sx={{ width: 28, height: 28, fontSize: '0.75rem', bgcolor: 'grey.300', color: 'text.primary' }}
                        >
                          {employee ? getEmployeeInitials(employee) : ''}
                        </Avatar>
                      )}
                      <Typography variant="body1" fontWeight={600}>
                        {label}
                      </Typography>
                    </Stack>
                  );
                },
              }}
              sx={{
                maxWidth: 320,
                width: '100%',
                bgcolor: 'background.paper',
                borderRadius: 1.5,
                boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1.5,
                  minHeight: 36,
                  '& fieldset': { borderColor: 'divider' },
                  '&:hover fieldset': { borderColor: 'text.secondary' },
                  '&.Mui-focused fieldset': { borderColor: 'primary.main', borderWidth: 1.5 },
                },
                '& .MuiInputLabel-root': { fontWeight: 600, fontSize: '0.95rem' },
                '& .MuiSelect-select': { fontSize: '0.98rem' },
                '& .MuiSelect-icon': { color: 'text.secondary' },
              }}
            >
              <MenuItem value={ALL_EMPLOYEES_OPTION}>
                <Stack direction="row" alignItems="center" spacing={1.25}>
                  <Typography variant="body1" fontWeight={600}>
                    {`All ${resourcePlural}`}
                  </Typography>
                </Stack>
              </MenuItem>
              {employees.map((employee) => (
                <MenuItem key={employee.id} value={employee.id}>
                  <Stack direction="row" alignItems="center" spacing={1.25}>
                    <Avatar
                      src={profilePictures[employee.id]}
                      sx={{ width: 28, height: 28, fontSize: '0.75rem', bgcolor: 'grey.300', color: 'text.primary' }}
                    >
                      {getEmployeeInitials(employee)}
                    </Avatar>
                    <Typography variant="body1" fontWeight={600}>
                      {employee.firstName} {employee.lastName}
                    </Typography>
                  </Stack>
                </MenuItem>
              ))}
            </TextField>
          </Box>
        )}
        {showEmployeeSelector && <Box sx={{ height: { xs: 4, md: 8 } }} />}

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', md: 'flex-start' }}
        >
          <Paper
            variant="outlined"
            sx={{
              flex: 1,
              minWidth: { xs: '100%', md: 360 },
              maxHeight: { xs: 'auto', md: 520 },
              overflow: 'auto',
            }}
          >
            {customersQuery.isLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" py={6}>
                <CircularProgress />
              </Box>
            ) : customers.length === 0 ? (
              <Box px={3} py={4}>
                <Typography variant="body1" color="text.secondary">
                  No customers found.
                </Typography>
              </Box>
            ) : (
              <List disablePadding>
                <ListItem disablePadding divider>
                  <Box px={2} py={1.5} width="100%">
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="Search..."
                      value={searchTerm}
                      onChange={(event) => setSearchTerm(event.target.value)}
                      InputProps={{
                        endAdornment: searchTerm ? (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              aria-label="Clear search"
                              onClick={() => setSearchTerm('')}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ) : undefined,
                      }}
                    />
                  </Box>
                </ListItem>
                {filteredCustomers.length === 0 ? (
                  <ListItem>
                    <ListItemText
                      primary="No matching customers."
                      primaryTypographyProps={{ color: 'text.secondary' }}
                    />
                  </ListItem>
                ) : (
                  filteredCustomers.map((customer) => {
                    const isSelected = customer.id === selectedCustomerId;
                    return (
                      <ListItem key={customer.id} disablePadding divider sx={{ display: 'block' }}>
                      <ListItemButton
                        selected={isSelected}
                        onClick={() =>
                          setSelectedCustomerId((current) => (current === customer.id ? null : customer.id))
                        }
                      >
                          <ListItemText
                            primary={`${customer.firstName} ${customer.lastName}`.trim() || formatPhoneDisplay(customer.phone)}
                            secondary={formatPhoneDisplay(customer.phone)}
                            primaryTypographyProps={{ fontWeight: 600 }}
                          />
                        </ListItemButton>
                      {!isMdUp && (
                        <Collapse in={isSelected} timeout={200} unmountOnExit>
                          <Box px={2.5} pb={2} pt={1}>
                          <Box display="flex" flexDirection="column" gap={1.5}>
                            <Stack spacing={1}>
                              <Box>
                                <Typography variant="body2" color="text.secondary">
                                  {customer.email || 'No email on file'}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="body2" color="text.secondary">
                                  Total appointments
                                </Typography>
                                <Typography variant="subtitle1" fontWeight={700}>
                                  {customer.totalAppointments ?? 0}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="body2" color="text.secondary">
                                  Total completed revenue
                                </Typography>
                                <Typography variant="subtitle1" fontWeight={700}>
                                  {formatCurrency(customer.totalCompletedRevenue ?? 0)}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="body2" color="text.secondary">
                                  Last appointment
                                </Typography>
                                <Typography variant="subtitle1" fontWeight={700}>
                                  {formatLastAppointmentDate(customer)}
                                </Typography>
                              </Box>
                            </Stack>
                            <Box display="flex" justifyContent="center" flexWrap="wrap" gap={1.5} mt={0.5}>
                              <Button
                                variant="contained"
                                color="primary"
                                startIcon={<PhoneIcon />}
                                onClick={(e) => { e.stopPropagation(); handleCall(customer.phone); }}
                                disabled={!customer.phone}
                                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', px: 2, py: 1 }}
                              >
                                Call
                              </Button>
                              <Button
                                variant="contained"
                                color="primary"
                                startIcon={<SmsIcon />}
                                onClick={(e) => { e.stopPropagation(); handleText(customer.phone); }}
                                disabled={!customer.phone}
                                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', px: 2, py: 1 }}
                              >
                                Text
                              </Button>
                              <Button
                                variant="contained"
                                color="primary"
                                startIcon={<EmailIcon />}
                                onClick={(e) => { e.stopPropagation(); handleEmail(customer.email); }}
                                disabled={!customer.email}
                                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', px: 2, py: 1 }}
                              >
                                Email
                              </Button>
                            </Box>
                          </Box>
                          </Box>
                        </Collapse>
                      )}
                    </ListItem>
                  );
                })
                )}
              </List>
            )}
            {customersQuery.hasNextPage && (
              <Box display="flex" justifyContent="center" py={2}>
                <Button
                  variant="outlined"
                  onClick={() => customersQuery.fetchNextPage()}
                  disabled={customersQuery.isFetchingNextPage}
                >
                  {customersQuery.isFetchingNextPage ? 'Loading...' : 'Load more'}
                </Button>
              </Box>
            )}
          </Paper>

          {isMdUp && (
            <Paper variant="outlined" sx={{ flex: 1, p: 3, minHeight: 320, display: 'flex', flexDirection: 'column' }}>
              {!selectedCustomer ? (
                <Typography variant="body1" color="text.secondary">
                  Select a customer to view details.
                </Typography>
              ) : (
                <Box display="flex" flexDirection="column" height="100%" flexGrow={1}>
                  <Box>
                    <Typography variant="h6" fontWeight={700}>
                      {`${selectedCustomer.firstName} ${selectedCustomer.lastName}`.trim() || selectedCustomer.phone}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedCustomer.email || 'No email on file'}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mt: 2, flexGrow: 1 }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Total appointments
                      </Typography>
                      <Typography variant="h6" fontWeight={700}>
                        {selectedCustomer.totalAppointments ?? 0}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Total completed revenue
                      </Typography>
                      <Typography variant="h6" fontWeight={700}>
                        {formatCurrency(selectedCustomer.totalCompletedRevenue ?? 0)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Last appointment
                      </Typography>
                      <Typography variant="h6" fontWeight={700}>
                        {formatLastAppointmentDate(selectedCustomer)}
                      </Typography>
                    </Box>
                  </Stack>
                  <Box display="flex" justifyContent="center" flexWrap="wrap" gap={1.5} mt="auto" pt={2}>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<PhoneIcon />}
                      onClick={() => handleCall(selectedCustomer.phone)}
                      disabled={!selectedCustomer.phone}
                      sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', px: 2, py: 1 }}
                    >
                      Call
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<SmsIcon />}
                      onClick={() => handleText(selectedCustomer.phone)}
                      disabled={!selectedCustomer.phone}
                      sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', px: 2, py: 1 }}
                    >
                      Text
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<EmailIcon />}
                      onClick={() => handleEmail(selectedCustomer.email)}
                      disabled={!selectedCustomer.email}
                      sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', px: 2, py: 1 }}
                    >
                      Email
                    </Button>
                  </Box>
                </Box>
              )}
            </Paper>
          )}
        </Stack>
      </Stack>
    </Box>
  );
};

export default CustomersPage;
